import React, { useEffect, useState } from 'react';
import { locomotorasService } from '../../services/api';
import api from '../../services/api';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  LineElement, PointElement, Tooltip, Legend, Filler
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Tooltip, Legend, Filler);

const CONSUMO_DIESEL = 1.13;
const ALERTA_PCT = 20;

function prediccionDiesel(litros, kmDia, consumo, dias = 15) {
  const litrosDia = (kmDia || 300) * (consumo || CONSUMO_DIESEL);
  const hoy = new Date();
  return Array.from({ length: dias }, (_, i) => {
    const fecha = new Date(hoy);
    fecha.setDate(hoy.getDate() + i + 1);
    return {
      fecha: fecha.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' }),
      litros: Math.max(0, litros - litrosDia * (i + 1))
    };
  });
}

function TarjetaCombustible({ loco, onActualizar }) {
  const [expandida, setExpandida] = useState(false);
  const cap = loco.capacidad_combustible || 5000;
  const litros = loco.litros_combustible || 0;
  const pct = Math.min(100, (litros / cap) * 100);
  const kmDia = loco.servicio_asignado?.km_aproximados || 300;
  const consumo = loco.consumo_estimado_lkm || CONSUMO_DIESEL;
  const litrosDia = kmDia * consumo;
  const diasRestantes = litros > 0 ? litros / litrosDia : 0;

  const color = pct < ALERTA_PCT ? '#a32d2d' : pct < 40 ? '#854f0b' : '#3b6d11';
  const bg = pct < ALERTA_PCT ? '#fcebeb' : pct < 40 ? '#faeeda' : '#eaf3de';
  const border = pct < ALERTA_PCT ? '#e24b4a' : pct < 40 ? '#ef9f27' : '#97c459';
  const estado = pct < ALERTA_PCT ? 'BAJO' : pct < 40 ? 'MEDIO' : 'OK';

  const pred = prediccionDiesel(litros, kmDia, consumo);

  const chartData = {
    labels: pred.map(p => p.fecha),
    datasets: [
      {
        type: 'bar',
        label: 'Litros estimados',
        data: pred.map(p => p.litros),
        backgroundColor: pred.map(p => {
          const pctP = (p.litros / cap) * 100;
          return pctP < ALERTA_PCT ? '#e24b4a' : pctP < 40 ? '#ef9f27' : '#639922';
        }),
        borderRadius: 4,
      },
      {
        type: 'line',
        label: 'Umbral alerta 20%',
        data: Array(15).fill(cap * 0.2),
        borderColor: '#ef9f27',
        borderDash: [4, 4],
        borderWidth: 1.5,
        pointRadius: 0,
      }
    ]
  };

  const chartOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { ticks: { color: '#888', font: { size: 10 }, maxRotation: 45 }, grid: { color: 'rgba(0,0,0,0.05)' } },
      y: { ticks: { color: '#888', font: { size: 11 } }, grid: { color: 'rgba(0,0,0,0.05)' }, min: 0, max: cap }
    }
  };

  return (
    <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${estado !== 'OK' ? border : '#e5e5e5'}`, overflow: 'hidden', marginBottom: 12 }}>
      <div style={{ padding: '16px 18px', cursor: 'pointer' }} onClick={() => setExpandida(!expandida)}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 16 }}>{loco.numero}</div>
            <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
              {loco.servicio_asignado ? loco.servicio_asignado.codigo : 'Sin servicio'} · {litrosDia.toFixed(0)} L/día est.
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ background: bg, color, border: `1px solid ${border}`, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 500 }}>
              {estado} · {Math.round(diasRestantes)}d
            </span>
            <span style={{ color: '#aaa', fontSize: 16 }}>{expandida ? '▲' : '▼'}</span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1, height: 8, background: '#f0f0f0', borderRadius: 4 }}>
            <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 0.3s' }} />
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, color, minWidth: 80, textAlign: 'right' }}>{litros.toLocaleString('es-ES', { maximumFractionDigits: 0 })} L</div>
          <div style={{ fontSize: 11, color: '#aaa', minWidth: 35 }}>{Math.round(pct)}%</div>
        </div>

        <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
          <div style={{ fontSize: 12, color: '#888' }}>Cap: <strong style={{ color: '#333' }}>{cap.toLocaleString('es-ES')} L</strong></div>
          <div style={{ fontSize: 12, color: '#888' }}>KM: <strong style={{ color: '#333' }}>{(loco.km_actuales || 0).toLocaleString('es-ES')}</strong></div>
          <div style={{ fontSize: 12, color: '#888' }}>Agota: <strong style={{ color }}>{new Date(Date.now() + diasRestantes * 86400000).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' })}</strong></div>
        </div>
      </div>

      {expandida && (
        <div style={{ borderTop: '1px solid #f0f0f0', padding: '16px 18px' }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, justifyContent: 'flex-end' }}>
            <button onClick={() => onActualizar(loco)} style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: '#1a1a1a', color: '#fff', cursor: 'pointer', fontSize: 13 }}>
              ⛽ Actualizar nivel
            </button>
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: '#555', marginBottom: 8 }}>Prediccion 15 dias</div>
            <div style={{ height: 180 }}>
              <Bar data={chartData} options={chartOptions} />
            </div>
          </div>

          <div>
            <div style={{ fontSize: 12, fontWeight: 500, color: '#555', marginBottom: 8 }}>Proximos 15 dias</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
              {pred.map((p, i) => {
                const pctP = (p.litros / cap) * 100;
                return (
                  <div key={i} style={{ background: pctP < ALERTA_PCT ? '#fcebeb' : pctP < 40 ? '#faeeda' : '#f8f8f6', borderRadius: 6, padding: '6px 8px', fontSize: 11 }}>
                    <div style={{ color: '#888' }}>{p.fecha}</div>
                    <div style={{ fontWeight: 500, color: pctP < ALERTA_PCT ? '#a32d2d' : pctP < 40 ? '#854f0b' : '#333' }}>
                      {p.litros.toFixed(0)} L · {Math.round(pctP)}%
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Combustible() {
  const [locomotoras, setLocomotoras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => { cargar(); }, []);

  const cargar = () => {
    setLoading(true);
    locomotorasService.getAll()
      .then(r => setLocomotoras(r.data.filter(l => l.activa)))
      .finally(() => setLoading(false));
  };

  const abrirModal = (loco) => {
    setModal(loco);
    setForm({
      litros_combustible: loco.litros_combustible || '',
      km: loco.km_actuales || '',
      notas: ''
    });
  };

  const guardar = async () => {
    if (!form.litros_combustible) { setMsg('Introduce los litros'); return; }
    setSaving(true);
    try {
      await locomotorasService.update(modal.id, {
        litros_combustible: parseFloat(form.litros_combustible),
        km_actuales: form.km ? parseFloat(form.km) : undefined,
      });
      setMsg('Guardado correctamente');
      setModal(null);
      cargar();
      setTimeout(() => setMsg(''), 3000);
    } catch (e) {
      setMsg('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 40, color: '#888' }}>Cargando...</div>;

  const bajos = locomotoras.filter(l => ((l.litros_combustible || 0) / (l.capacidad_combustible || 5000)) * 100 < ALERTA_PCT);

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 22, fontWeight: 500, margin: 0 }}>Control Combustible</h2>
        <p style={{ fontSize: 13, color: '#888', marginTop: 4 }}>Nivel de diesel por locomotora · alerta bajo 20%</p>
      </div>

      {msg && <div style={{ background: '#eaf3de', border: '1px solid #97c459', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 13, color: '#3b6d11' }}>{msg}</div>}

      {bajos.length > 0 && (
        <div style={{ background: '#fcebeb', border: '1px solid #e24b4a', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#a32d2d', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>⚠</span>
          <strong>Nivel bajo:</strong> {bajos.map(l => l.numero).join(', ')}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
        {locomotoras
          .sort((a, b) => ((a.litros_combustible || 0) / (a.capacidad_combustible || 5000)) - ((b.litros_combustible || 0) / (b.capacidad_combustible || 5000)))
          .map(l => <TarjetaCombustible key={l.id} loco={l} onActualizar={abrirModal} />)
        }
      </div>

      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}
          onClick={e => { if (e.target === e.currentTarget) setModal(null); }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 380, maxWidth: '95vw' }}>
            <h3 style={{ fontSize: 16, fontWeight: 500, marginBottom: 6 }}>⛽ Actualizar combustible — {modal.numero}</h3>
            <p style={{ fontSize: 12, color: '#888', marginBottom: 20 }}>
              Capacidad: {(modal.capacidad_combustible || 5000).toLocaleString('es-ES')} L · Actual: {(modal.litros_combustible || 0).toFixed(0)} L
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Litros actuales</label>
                <input type="number" value={form.litros_combustible} onChange={e => setForm({ ...form, litros_combustible: e.target.value })}
                  placeholder="Ej: 4200"
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>KM odometro</label>
                <input type="number" value={form.km} onChange={e => setForm({ ...form, km: e.target.value })}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }} />
              </div>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Notas (opcional)</label>
              <input type="text" value={form.notas} onChange={e => setForm({ ...form, notas: e.target.value })}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }} />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setModal(null)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: 13 }}>Cancelar</button>
              <button onClick={guardar} disabled={saving} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#1a1a1a', color: '#fff', cursor: 'pointer', fontSize: 13, opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
